import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { sanitizeWordHtml } from '../../utils/WordPasteSanitizer';
import wordTemplateProcessor from '../../utils/WordTemplateProcessor';
import './RadiologyRichEditor.css';

/**
 * RadiologyRichEditor
 * Specialized browser-based rich text document editor for diagnostic radiology.
 * Handles headings, bold/italic/underline, lists, tables, alignment, undo/redo,
 * full-screen mode, keyboard navigation (Tab/Enter), smart macros (/renal, /gb, /normal),
 * and seamlessly sanitizes Word/Outlook copy-paste content without layout corruption.
 */

const FONT_FAMILY_OPTIONS = [
  { label: 'Calibri', value: 'Calibri, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Cambria', value: 'Cambria, serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Trebuchet MS', value: "'Trebuchet MS', sans-serif" },
  { label: 'Courier New', value: "'Courier New', monospace" },
  { label: 'Segoe UI', value: "'Segoe UI', sans-serif" }
];

const FONT_SIZE_OPTIONS = [10, 11, 12, 14, 16, 18, 24];

const LINE_SPACING_OPTIONS = [
  { label: 'Single', value: '1' },
  { label: '1.15', value: '1.15' },
  { label: '1.5', value: '1.5' },
  { label: 'Double', value: '2' }
];

const BLOCK_TAGS = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TD', 'TH', 'BLOCKQUOTE'];

const RadiologyRichEditor = forwardRef(function RadiologyRichEditor({
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Enter report details...',
  minHeight = '220px',
  macros = []
}, ref) {
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const forcePlainPasteRef = useRef(false);
  const findInputRef = useRef(null);
  // Last selection Range inside this editor — kept so an external caller (voice dictation)
  // can insert text at the user's last cursor position even after focus has moved to
  // another control (e.g. the Dictate button), instead of always landing at the end.
  const savedRangeRef = useRef(null);

  const saveCurrentRange = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  }, []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [plainPasteArmed, setPlainPasteArmed] = useState(false);

  // Find-in-report state (Part C.8 / D)
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findMatches, setFindMatches] = useState([]);
  const [findActiveIndex, setFindActiveIndex] = useState(-1);

  // Default built-in macros combined with doctor personal/centre macros
  const defaultMacros = [
    { shortcut: '/normal', title: 'Normal Exam', replacementText: '<p>• Normal examination without focal abnormality detected.</p>' },
    { shortcut: '/renal', title: 'Kidneys Normal', replacementText: '<p>• Both kidneys are normal in size, shape, and cortical thickness. Corticomedullary differentiation is preserved. No calculus, mass, or hydronephrosis.</p>' },
    { shortcut: '/gb', title: 'GB Normal', replacementText: '<p>• Gallbladder is well distended with normal wall thickness (< 3 mm). Lumen is clear without calculus or sludge.</p>' },
    { shortcut: '/chest_normal', title: 'Chest Clear', replacementText: '<p>• Trachea is midline.<br>• Both lung fields are clear without focal infiltrates, consolidation, or effusion.<br>• Cardiothoracic ratio is normal (< 0.50). Costophrenic angles are clear.</p>' },
    { shortcut: '/brain_normal', title: 'Brain Normal', replacementText: '<p>• Normal gray-white matter differentiation.<br>• Ventricles and sulcal spaces are within normal limits for age.<br>• No acute territorial infarct, intracranial hemorrhage, or mass effect.</p>' }
  ];

  const allMacros = [...defaultMacros, ...(Array.isArray(macros) ? macros : [])];

  // Sync external value changes into the contentEditable DOM
  useEffect(() => {
    if (!editorRef.current) return;
    const currentHtml = editorRef.current.innerHTML;
    // Normalize empty strings vs <br> / <p><br></p>
    const normCurrent = currentHtml.trim() === '<p><br></p>' || currentHtml.trim() === '<br>' ? '' : currentHtml.trim();
    const normValue = (value || '').trim();

    if (normCurrent !== normValue && !isUpdatingRef.current) {
      editorRef.current.innerHTML = sanitizeHtml(normValue || '');
    }
  }, [value]);

  // Emit HTML changes
  const handleInput = useCallback(() => {
    if (!editorRef.current || !onChange) return;
    isUpdatingRef.current = true;
    const html = editorRef.current.innerHTML;
    onChange(html);
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 50);
  }, [onChange]);

  // Insert text at the last known cursor position in this editor (used by voice dictation,
  // which loses DOM focus to the Dictate button before each transcript chunk arrives).
  // Falls back to appending at the end only if no prior cursor position was ever captured.
  const insertTextAtCursor = useCallback((text) => {
    if (disabled || !editorRef.current || !text) return;
    editorRef.current.focus();

    const sel = window.getSelection();
    if (!savedRangeRef.current) {
      // No cursor was ever placed inside the editor yet — collapse to the end so the
      // very first dictated chunk still lands somewhere sensible instead of being dropped.
      const endRange = document.createRange();
      endRange.selectNodeContents(editorRef.current);
      endRange.collapse(false);
      savedRangeRef.current = endRange;
    }
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }

    document.execCommand('insertText', false, text);

    const newSel = window.getSelection();
    if (newSel && newSel.rangeCount > 0) {
      savedRangeRef.current = newSel.getRangeAt(0).cloneRange();
    }

    handleInput();
  }, [disabled, handleInput]);

  useImperativeHandle(ref, () => ({ insertTextAtCursor }), [insertTextAtCursor]);

  // Execute formatting command
  const execCmd = (command, val = null) => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, val);
    handleInput();
  };

  // Insert macro HTML at current cursor position
  const insertMacro = (macro) => {
    if (disabled || !editorRef.current || !macro) return;
    editorRef.current.focus();
    const textToInsert = macro.replacementText || macro.text || '';
    document.execCommand('insertHTML', false, textToInsert);
    handleInput();
  };

  // ── Font size (Part C.2) ──────────────────────────────────────────────────
  // execCommand('fontSize', ...) only supports legacy 1-7 sizing. We apply command '7'
  // (the largest legacy bucket, producing a single easily-identifiable <font size="7"> wrapper),
  // then immediately replace that wrapper with a <span style="font-size:...px"> so the editor
  // supports real pixel sizes. Selection is restored over the new span so, like the native
  // execCommand-based buttons, the user doesn't lose their place.
  const applyFontSize = (px) => {
    if (disabled || !editorRef.current || !px) return;
    editorRef.current.focus();
    document.execCommand('fontSize', false, '7');

    const fontEls = Array.from(editorRef.current.querySelectorAll('font[size="7"]'));
    let lastSpan = null;
    fontEls.forEach((f) => {
      const span = document.createElement('span');
      span.style.fontSize = `${px}px`;
      while (f.firstChild) span.appendChild(f.firstChild);
      if (f.parentNode) f.parentNode.replaceChild(span, f);
      lastSpan = span;
    });

    if (lastSpan) {
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(lastSpan);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    handleInput();
  };

  // ── Highlight / text color (Part C.3) ─────────────────────────────────────
  const applyForeColor = (color) => execCmd('foreColor', color);

  const applyHighlightColor = (color) => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();
    let supported = false;
    try {
      supported = !!(document.queryCommandSupported && document.queryCommandSupported('hiliteColor'));
    } catch (e) {
      supported = false;
    }
    try {
      if (supported) {
        document.execCommand('hiliteColor', false, color);
      } else {
        document.execCommand('backColor', false, color);
      }
    } catch (e) {
      try { document.execCommand('backColor', false, color); } catch (e2) { /* no-op */ }
    }
    handleInput();
  };

  // ── Line spacing (Part C.7) ────────────────────────────────────────────────
  // execCommand('lineHeight', ...) isn't universally supported, so we find the closest
  // block-level ancestor of the current selection and set its style directly.
  const getBlockAncestor = (node) => {
    let el = node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el && el !== editorRef.current && !BLOCK_TAGS.includes(el.tagName)) {
      el = el.parentElement;
    }
    return el && el !== editorRef.current ? el : null;
  };

  const applyLineSpacing = (value) => {
    if (disabled || !editorRef.current || !value) return;
    editorRef.current.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const block = getBlockAncestor(range.commonAncestorContainer);
    if (block) {
      block.style.lineHeight = value;
    } else {
      editorRef.current.style.lineHeight = value;
    }
    handleInput();
  };

  // ── Paste as plain text (Part C.9 / D) ────────────────────────────────────
  // Shared by the toolbar button and Ctrl+Shift+V: both simply "arm" the next paste event to
  // skip the HTML branch in handlePaste, regardless of whether HTML clipboard data is present.
  const armPlainTextPaste = () => {
    if (disabled) return;
    forcePlainPasteRef.current = true;
    setPlainPasteArmed(true);
    editorRef.current?.focus();
  };

  // ── Find in report (Part C.8 / D) ─────────────────────────────────────────
  const clearFindHighlights = useCallback(() => {
    if (!editorRef.current) return;
    const marks = Array.from(editorRef.current.querySelectorAll('mark.rad-find-highlight'));
    marks.forEach((m) => {
      const parent = m.parentNode;
      if (!parent) return;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
      if (parent.normalize) parent.normalize();
    });
    setFindMatches([]);
    setFindActiveIndex(-1);
  }, []);

  const runFind = useCallback((query) => {
    clearFindHighlights();
    if (!editorRef.current || !query) return;

    const lowerQuery = query.toLowerCase();
    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let n = walker.nextNode();
    while (n) {
      textNodes.push(n);
      n = walker.nextNode();
    }

    const newMarks = [];
    textNodes.forEach((textNode) => {
      const text = textNode.textContent || '';
      const lowerText = text.toLowerCase();
      if (!lowerText.includes(lowerQuery)) return;

      const frag = document.createDocumentFragment();
      let cursor = 0;
      let idx = lowerText.indexOf(lowerQuery, cursor);
      while (idx !== -1) {
        if (idx > cursor) frag.appendChild(document.createTextNode(text.slice(cursor, idx)));
        const mark = document.createElement('mark');
        mark.className = 'rad-find-highlight';
        mark.textContent = text.slice(idx, idx + query.length);
        frag.appendChild(mark);
        newMarks.push(mark);
        cursor = idx + query.length;
        idx = lowerText.indexOf(lowerQuery, cursor);
      }
      if (cursor < text.length) frag.appendChild(document.createTextNode(text.slice(cursor)));

      if (textNode.parentNode) textNode.parentNode.replaceChild(frag, textNode);
    });

    setFindMatches(newMarks);
    if (newMarks.length > 0) {
      newMarks[0].classList.add('rad-find-active');
      if (newMarks[0].scrollIntoView) newMarks[0].scrollIntoView({ block: 'center' });
      setFindActiveIndex(0);
    } else {
      setFindActiveIndex(-1);
    }
  }, [clearFindHighlights]);

  const goToMatch = (index) => {
    if (findMatches.length === 0) return;
    const clamped = ((index % findMatches.length) + findMatches.length) % findMatches.length;
    findMatches.forEach((m) => m.classList.remove('rad-find-active'));
    findMatches[clamped].classList.add('rad-find-active');
    if (findMatches[clamped].scrollIntoView) findMatches[clamped].scrollIntoView({ block: 'center' });
    setFindActiveIndex(clamped);
  };

  const findNext = () => goToMatch(findActiveIndex + 1);
  const findPrev = () => goToMatch(findActiveIndex - 1);

  const openFind = () => {
    if (disabled) return;
    setFindOpen(true);
    setTimeout(() => findInputRef.current?.focus(), 0);
  };

  const closeFind = useCallback(() => {
    clearFindHighlights();
    setFindOpen(false);
    setFindQuery('');
  }, [clearFindHighlights]);

  const handleFindInputChange = (e) => {
    const q = e.target.value;
    setFindQuery(q);
    runFind(q);
  };

  const handleFindInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeFind();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) findPrev(); else findNext();
    }
  };

  // Handle Tab, macro slash shortcuts, and all new keyboard shortcuts
  const handleKeyDown = (e) => {
    if (disabled || !editorRef.current) return;

    const mod = e.ctrlKey || e.metaKey; // Support Cmd on macOS alongside Ctrl everywhere below

    // Escape: close the find bar if open, else exit fullscreen if active.
    // UI-state only — never discards saved/unsaved content.
    if (e.key === 'Escape') {
      if (findOpen) {
        e.preventDefault();
        closeFind();
        return;
      }
      if (isFullscreen) {
        e.preventDefault();
        setIsFullscreen(false);
        return;
      }
    }

    // Ctrl/Cmd+F: open find bar — scoped to this editor's own DOM node only
    if (mod && !e.shiftKey && (e.key === 'f' || e.key === 'F')) {
      const target = e.target;
      if (target === editorRef.current || (editorRef.current && editorRef.current.contains(target))) {
        e.preventDefault();
        openFind();
        return;
      }
    }

    // Ctrl/Cmd+Shift+V: force plain-text paste on the next paste event
    if (mod && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
      e.preventDefault();
      armPlainTextPaste();
      return;
    }

    // Ctrl/Cmd+Shift+X: strikethrough (no handler existed at all before this)
    if (mod && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      execCmd('strikeThrough');
      return;
    }

    // Ctrl/Cmd+L/E/R/J: alignment (not natively handled by contentEditable)
    if (mod && !e.shiftKey && !e.altKey) {
      if (e.key === 'l' || e.key === 'L') { e.preventDefault(); execCmd('justifyLeft'); return; }
      if (e.key === 'e' || e.key === 'E') { e.preventDefault(); execCmd('justifyCenter'); return; }
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); execCmd('justifyRight'); return; }
      if (e.key === 'j' || e.key === 'J') { e.preventDefault(); execCmd('justifyFull'); return; }
    }

    // 1. Tab key navigation: insert indentation rather than losing focus
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
      handleInput();
      return;
    }

    // 2. Expand macros on Space or Enter if user typed e.g. /normal or /renal
    if (e.key === ' ' || e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node && node.nodeType === Node.TEXT_NODE) {
          const textBeforeCursor = node.textContent.substring(0, range.startOffset);
          const slashMatch = textBeforeCursor.match(/(\/[a-zA-Z0-9_]+)$/);
          if (slashMatch) {
            const typedShortcut = slashMatch[1].toLowerCase();
            const matchedMacro = allMacros.find(m => m.shortcut?.toLowerCase() === typedShortcut);
            if (matchedMacro) {
              e.preventDefault();
              // Delete the typed shortcut text
              const startIndex = range.startOffset - typedShortcut.length;
              range.setStart(node, startIndex);
              range.setEnd(node, range.startOffset);
              range.deleteContents();

              // Insert the macro expansion
              document.execCommand('insertHTML', false, matchedMacro.replacementText || matchedMacro.text || '');
              handleInput();
              return;
            }
          }
        }
      }
    }
  };

  const [pasteToast, setPasteToast] = useState(null);

  // Intercept paste to process Word/Outlook HTML with layout preservation
  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();

    const clipboardData = e.clipboardData || window.clipboardData;
    const forcePlain = forcePlainPasteRef.current;
    forcePlainPasteRef.current = false;
    setPlainPasteArmed(false);

    const pastedHtml = forcePlain ? null : clipboardData.getData('text/html');
    const pastedText = clipboardData.getData('text/plain');

    if (pastedHtml) {
      const result = sanitizeWordHtml(pastedHtml);
      document.execCommand('insertHTML', false, result);

      // Check for Word content and notify user
      const fullResult = wordTemplateProcessor.process(pastedHtml);
      if (fullResult.stats.isWord || fullResult.stats.tablesCount > 0) {
        let msg = '📋 Word Template Formatted';
        const details = [];
        if (fullResult.stats.tablesCount > 0) details.push(`${fullResult.stats.tablesCount} table${fullResult.stats.tablesCount > 1 ? 's' : ''}`);
        if (fullResult.stats.mergedCellsCount > 0) details.push(`${fullResult.stats.mergedCellsCount} merged cell${fullResult.stats.mergedCellsCount > 1 ? 's' : ''}`);
        if (fullResult.stats.placeholdersCount > 0) details.push(`${fullResult.stats.placeholdersCount} placeholder${fullResult.stats.placeholdersCount > 1 ? 's' : ''}`);
        if (details.length > 0) msg += ` (${details.join(', ')} preserved)`;
        setPasteToast(msg);
        setTimeout(() => setPasteToast(null), 4500);
      }
    } else if (pastedText) {
      // Convert plain text newlines into paragraphs or line breaks
      const htmlFromText = pastedText
        .split(/\r?\n/)
        .map(line => line.trim() ? `<div>${escapeHtml(line)}</div>` : '<div><br></div>')
        .join('');
      document.execCommand('insertHTML', false, htmlFromText);
    }
    handleInput();
  };

  // Helper to escape plain text
  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.innerText = text;
    return div.innerHTML;
  };

  // Insert standard 2x2 clinical comparison table
  const insertTable = (rows = 2, cols = 2) => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();

    let tableHtml = '<table class="rad-doc-table" style="width: 100%; border-collapse: collapse; margin: 8px 0;"><tbody>';
    for (let r = 0; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        tableHtml += `<td style="border: 1px solid #cbd5e1; padding: 6px 10px; min-width: 80px;">${r === 0 ? `<strong>Col ${c + 1}</strong>` : 'Cell'}</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table><p><br></p>';

    document.execCommand('insertHTML', false, tableHtml);
    handleInput();
  };

  return (
    <div className={`rad-rich-editor ${disabled ? 'disabled' : ''} ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Editor Toolbar */}
      {!disabled && (
        <div className="rad-editor-toolbar">
          <div className="rad-toolbar-group">
            <button
              type="button"
              className="rad-tool-btn"
              title="Undo (Ctrl+Z)"
              onClick={() => execCmd('undo')}
            >
              ↩
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Redo (Ctrl+Y)"
              onClick={() => execCmd('redo')}
            >
              ↪
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <select
              className="rad-tool-select"
              title="Formatting Block"
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'p') execCmd('formatBlock', '<p>');
                else if (val) execCmd('formatBlock', `<${val}>`);
                e.target.value = '';
              }}
              defaultValue=""
            >
              <option value="" disabled>Style...</option>
              <option value="p">Normal Paragraph</option>
              <option value="h2">Section Heading (H2)</option>
              <option value="h3">Subsection (H3)</option>
              <option value="h4">Minor Header (H4)</option>
            </select>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <select
              className="rad-tool-select rad-tool-select-font"
              title="Font Family"
              aria-label="Font Family"
              onChange={(e) => {
                const val = e.target.value;
                if (val) execCmd('fontName', val);
                e.target.value = '';
              }}
              defaultValue=""
            >
              <option value="" disabled>Font...</option>
              {FONT_FAMILY_OPTIONS.map(f => (
                <option key={f.label} value={f.value}>{f.label}</option>
              ))}
            </select>

            <select
              className="rad-tool-select rad-tool-select-size"
              title="Font Size"
              aria-label="Font Size"
              onChange={(e) => {
                const val = e.target.value;
                if (val) applyFontSize(parseInt(val, 10));
                e.target.value = '';
              }}
              defaultValue=""
            >
              <option value="" disabled>Size...</option>
              {FONT_SIZE_OPTIONS.map(s => (
                <option key={s} value={s}>{s}px</option>
              ))}
            </select>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <button
              type="button"
              className="rad-tool-btn bold"
              title="Bold (Ctrl+B)"
              onClick={() => execCmd('bold')}
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              className="rad-tool-btn italic"
              title="Italic (Ctrl+I)"
              onClick={() => execCmd('italic')}
            >
              <em>I</em>
            </button>
            <button
              type="button"
              className="rad-tool-btn underline"
              title="Underline (Ctrl+U)"
              onClick={() => execCmd('underline')}
            >
              <u>U</u>
            </button>
            <button
              type="button"
              className="rad-tool-btn strike"
              title="Strikethrough (Ctrl+Shift+X)"
              onClick={() => execCmd('strikeThrough')}
            >
              <s>S</s>
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Superscript"
              onClick={() => execCmd('superscript')}
            >
              X<sup>2</sup>
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Subscript"
              onClick={() => execCmd('subscript')}
            >
              X<sub>2</sub>
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <label className="rad-color-swatch" title="Font Color">
              <span className="rad-color-swatch-icon">A</span>
              <input
                type="color"
                aria-label="Font Color"
                onChange={(e) => applyForeColor(e.target.value)}
                defaultValue="#1e293b"
              />
            </label>
            <label className="rad-color-swatch" title="Highlight Color">
              <span className="rad-color-swatch-icon">🖍</span>
              <input
                type="color"
                aria-label="Highlight Color"
                onChange={(e) => applyHighlightColor(e.target.value)}
                defaultValue="#fef08a"
              />
            </label>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <button
              type="button"
              className="rad-tool-btn"
              title="Align Left (Ctrl+L)"
              onClick={() => execCmd('justifyLeft')}
            >
              ⇤
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Align Center (Ctrl+E)"
              onClick={() => execCmd('justifyCenter')}
            >
              ≡
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Align Right (Ctrl+R)"
              onClick={() => execCmd('justifyRight')}
            >
              ⇥
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Justify (Ctrl+J)"
              onClick={() => execCmd('justifyFull')}
            >
              ≣
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <button
              type="button"
              className="rad-tool-btn"
              title="Bulleted List"
              onClick={() => execCmd('insertUnorderedList')}
            >
              • List
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Numbered List"
              onClick={() => execCmd('insertOrderedList')}
            >
              1. List
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Outdent"
              onClick={() => execCmd('outdent')}
            >
              ⇇
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Indent"
              onClick={() => execCmd('indent')}
            >
              ⇉
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <select
              className="rad-tool-select"
              title="Line Spacing"
              aria-label="Line Spacing"
              onChange={(e) => {
                const val = e.target.value;
                if (val) applyLineSpacing(val);
                e.target.value = '';
              }}
              defaultValue=""
            >
              <option value="" disabled>Spacing...</option>
              {LINE_SPACING_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <button
              type="button"
              className="rad-tool-btn"
              title="Insert Table (2x2)"
              onClick={() => insertTable(2, 2)}
            >
              ▦ Table
            </button>
            <button
              type="button"
              className="rad-tool-btn"
              title="Clear Formatting"
              onClick={() => execCmd('removeFormat')}
            >
              🧹 Clear
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          <div className="rad-toolbar-group">
            <button
              type="button"
              className={`rad-tool-btn ${findOpen ? 'active' : ''}`}
              title="Find in Report (Ctrl+F)"
              onClick={() => (findOpen ? closeFind() : openFind())}
            >
              🔍 Find
            </button>
            <button
              type="button"
              className={`rad-tool-btn ${plainPasteArmed ? 'active' : ''}`}
              title="Paste as Plain Text (Ctrl+Shift+V) — arms the next paste"
              onClick={armPlainTextPaste}
            >
              📋 Plain Paste
            </button>
          </div>

          <div className="rad-toolbar-divider" />

          {/* Fullscreen Toggle */}
          <div className="rad-toolbar-group">
            <button
              type="button"
              className={`rad-tool-btn ${isFullscreen ? 'active' : ''}`}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Editor'}
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? '🗗 Exit' : '⛶ Fullscreen'}
            </button>
          </div>
        </div>
      )}

      {/* Find in Report bar */}
      {findOpen && (
        <div className="rad-find-bar">
          <span className="rad-find-icon">🔍</span>
          <input
            ref={findInputRef}
            type="text"
            className="rad-find-input"
            placeholder="Find in report..."
            value={findQuery}
            onChange={handleFindInputChange}
            onKeyDown={handleFindInputKeyDown}
          />
          <span className="rad-find-count">
            {findQuery ? (findMatches.length > 0 ? `${findActiveIndex + 1} of ${findMatches.length}` : '0 of 0') : ''}
          </span>
          <button type="button" className="rad-find-nav-btn" title="Previous match" onClick={findPrev} disabled={findMatches.length === 0}>↑</button>
          <button type="button" className="rad-find-nav-btn" title="Next match" onClick={findNext} disabled={findMatches.length === 0}>↓</button>
          <button type="button" className="rad-find-close-btn" title="Close (Esc)" onClick={closeFind}>×</button>
        </div>
      )}

      {/* Word Paste Success Feedback Banner */}
      {pasteToast && (
        <div className="rad-paste-toast">
          <span className="rad-paste-toast-icon">✓</span>
          <span className="rad-paste-toast-text">{pasteToast}</span>
          <button type="button" className="rad-paste-toast-close" onClick={() => setPasteToast(null)}>×</button>
        </div>
      )}

      {/* Plain-paste-armed feedback */}
      {plainPasteArmed && (
        <div className="rad-paste-toast rad-plain-paste-toast">
          <span className="rad-paste-toast-icon">📋</span>
          <span className="rad-paste-toast-text">Plain-text paste armed — press Ctrl+V now to paste without formatting.</span>
          <button type="button" className="rad-paste-toast-close" onClick={() => { forcePlainPasteRef.current = false; setPlainPasteArmed(false); }}>×</button>
        </div>
      )}

      {/* Smart Macros Quick Chips Bar */}
      {!disabled && allMacros.length > 0 && (
        <div className="rad-macro-chips-bar">
          <span className="rad-macro-label">⚡ Macros:</span>
          <div className="rad-macro-chips-list">
            {allMacros.slice(0, 7).map((m, idx) => (
              <button
                key={idx}
                type="button"
                className="rad-macro-chip"
                onClick={() => insertMacro(m)}
                title={`Type '${m.shortcut}' or click to insert`}
              >
                <span>{m.shortcut}</span> {m.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Editable Canvas */}
      <div
        ref={editorRef}
        className="rad-editor-canvas"
        contentEditable={!disabled && !findOpen}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={saveCurrentRange}
        onMouseUp={saveCurrentRange}
        onPaste={handlePaste}
        style={{ minHeight: isFullscreen ? '75vh' : minHeight }}
        data-placeholder={placeholder}
      />
    </div>
  );
});

export default RadiologyRichEditor;
